// @flow
import {useState, useEffect, useCallback, useRef} from 'react';

/**
 * Custom hook to monitor user activity
 * @param {Object} options - Configuration options
 * @param {number} options.timeout - Inactivity timeout in milliseconds (default: 5 minutes)
 * @param {Array} options.events - Events to monitor (default: mouse, keyboard, scroll)
 * @param {Function} options.onActive - Callback when user becomes active
 * @param {Function} options.onInactive - Callback when user becomes inactive
 * @param {boolean} options.throttle - Whether to throttle events (default: true)
 * @param {number} options.throttleMs - Throttle delay in milliseconds (default: 1000)
 * @returns {Object} - { isActive, lastActivity, resetTimer }
 */
export const useUserActivity = (options = {}) => {
    const {
        timeout = 5 * 60 * 1000, // 5-minutes default
        events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
        onActive,
        onInactive,
        throttle = true,
        throttleMs = 2000
    } = options;

    const [isActive, setIsActive] = useState(true);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const timeoutRef = useRef(null);
    const throttleRef = useRef(null);

    // Reset the inactivity timer
    // We reset this timer every time the user becomes active
    const resetTimer = useCallback(() => {
        const now = Date.now();
        setLastActivity(now);

        if (!isActive) { // switching to active
            console.debug("useUserActivity: activity timeout reached");

            setIsActive(true);
            onActive && onActive();
        }

        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if(timeout===0) {
            console.debug("useUserActivity: inactivity timeout disabled");
            return;
        }
        // Set a new timeout
        timeoutRef.current = setTimeout(() => { // we are considered inactive if this timer fires
            console.debug("useUserActivity: inactivity timeout reached");
            timeoutRef.current = null;
            setIsActive(false);
            onInactive && onInactive();
        }, timeout);
    }, [isActive, timeout, onActive, onInactive]);

    // Throttled version of resetTimer
    const throttledResetTimer = useCallback(() => {
        if (!throttle) {
            console.debug("useUserActivity: throttling disabled");
            resetTimer();
            return;
        }

        if (throttleRef.current) { // called every event!
            return; // Already throttled
        }

        console.debug("useUserActivity: throttling");
        resetTimer();
        throttleRef.current = setTimeout(() => {
            throttleRef.current = null;
        }, throttleMs);
    }, [resetTimer, throttle, throttleMs]);

    useEffect(() => {

        if(timeout===0) {
            console.debug("useUserActivity: inactivity timeout disabled");
            return;
        }
        
        console.debug("useUserActivity setting up - timeout: " + timeout + ", events: " + events);
        events.forEach(event => {
            document.addEventListener(event, throttledResetTimer, true);
        });

        // Initialize timer
        resetTimer();

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, throttledResetTimer, true);
            });

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            if (throttleRef.current) {
                clearTimeout(throttleRef.current);
            }
        };
    }, [timeout]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        isActive, lastActivity, resetTimer
    };
};
